/**
 * Typed search params for the `/users` (People & Access) route. The text search + the
 * role filter live in the URL so they are deep-linkable AND compile-checked (the
 * employees/estimates `searchParams` pattern). TanStack Router validates the raw URL
 * params through `validateUsersSearch` on every navigation.
 *
 * `?q=<text>` filters the list by name/email (client-side over the coarse payload — the
 * directory is small and already fully loaded; no extra round-trip). `?role=<Role>`
 * narrows the list to one role (Admin/Manager/Employee), driving the role-filter pills.
 * The coarse endpoint owns the role + active counts; the SPA carries the flags and does
 * the text + role filter in-memory (the directory is already fully loaded).
 */

export type UserRole = 'Admin' | 'Manager' | 'Employee';

export const USER_ROLES: readonly UserRole[] = ['Admin', 'Manager', 'Employee'] as const;

export interface UsersSearch {
  q?: string;
  role?: UserRole;
}

/** Coerce a raw URL value into a trimmed, length-capped search string (else undefined). */
function asQuery(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, 100);
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Coerce a raw URL value into a known role (else undefined → no role filter). */
function asRole(value: unknown): UserRole | undefined {
  return USER_ROLES.includes(value as UserRole) ? (value as UserRole) : undefined;
}

/**
 * TanStack Router `validateSearch` for the users list route. Keeps the search text only
 * when non-empty and the role only when a known role, so the canonical URL stays clean
 * (`/users` vs `/users?q=alice` vs `/users?role=Admin`).
 */
export function validateUsersSearch(raw: Record<string, unknown>): UsersSearch {
  const search: UsersSearch = {};
  const q = asQuery(raw.q);
  if (q) search.q = q;
  const role = asRole(raw.role);
  if (role) search.role = role;
  return search;
}
